import { create } from 'zustand';
import type { 
  RoomDTO, 
  PlayerDTO, 
  UserId,
  // QuizSettings,
  LeaderboardEntry,
  RoundResults,
} from '@beshek/shared';

// ============================================
// App State
// ============================================

type AppScreen = 
  | 'home'
  | 'join'
  | 'lobby'
  | 'game'
  | 'results'
  | 'finished';

type ClientRole = 'player' | 'screen';

interface AppState {
  // Auth
  userId: UserId | null;
  displayName: string;
  role: ClientRole;
  token: string;

  // Navigation
  screen: AppScreen;

  // Room
  room: RoomDTO | null;
  
  // Game
  gameId: string | null;
  gameStatus: 'idle' | 'preparing' | 'countdown' | 'question' | 'results' | 'finished';
  
  // Current Question
  currentRound: number;
  totalQuestions: number;
  resultsDisplayTime: number;
  questionId: string | null;
  questionText: string | null;
  options: string[];
  selectedAnswer: number | null;
  answerSent: boolean;
  timeLeft: number;
  
  // Results
  roundResults: RoundResults | null;
  leaderboard: LeaderboardEntry[];
  gameStats: { totalQuestions: number; duration: number; totalPlayers: number } | null;
  isCorrect: boolean | null;
  pointsEarned: number;

  // Errors
  error: string | null;
}

interface AppActions {
  // Auth
  setAuth: (userId: UserId, displayName: string, token: string) => void;
  setRole: (role: ClientRole) => void;

  // Navigation
  setScreen: (screen: AppScreen) => void;

  // Room
  setRoom: (room: RoomDTO | null) => void;
  addPlayer: (player: PlayerDTO) => void;
  removePlayer: (playerId: UserId) => void;
  updatePlayer: (player: PlayerDTO) => void;
  updatePlayerReady: (playerId: UserId, isReady: boolean) => void;

  // Game
  setGameStarted: (gameId: string, totalQuestions: number, resultsDisplayTime?: number) => void;
  setGameStatus: (status: AppState['gameStatus']) => void;
  setCountdown: (seconds: number) => void;
  
  // Question
  setQuestion: (data: { questionId: string; questionText?: string; options: string[]; round: number }) => void;
  selectAnswer: (index: number) => void;
  setAnswerSent: () => void;
  setTimeLeft: (ms: number) => void;
  
  // Results
  setRoundResults: (results: RoundResults, myPlayerId: UserId) => void;
  setGameFinished: (leaderboard: LeaderboardEntry[], stats?: { totalQuestions: number; duration: number; totalPlayers: number }) => void;

  // Errors
  setError: (error: string | null) => void;

  // Reset
  reset: () => void;
  resetGame: () => void;
}

const initialState: AppState = {
  userId: null,
  displayName: '',
  role: 'player',
  token: '',
  screen: 'home',
  room: null,
  gameId: null,
  gameStatus: 'idle',
  currentRound: 0,
  totalQuestions: 0,
  resultsDisplayTime: 12,
  questionId: null,
  questionText: null,
  options: [],
  selectedAnswer: null,
  answerSent: false,
  timeLeft: 0,
  roundResults: null,
  leaderboard: [],
  gameStats: null,
  isCorrect: null,
  pointsEarned: 0,
  error: null,
};

export const useAppStore = create<AppState & AppActions>((set, _get) => ({
  ...initialState,

  // Auth
  setAuth: (userId, displayName, token) => set({ userId, displayName, token }),
  setRole: (role) => set({ role }),

  // Navigation
  setScreen: (screen) => set({ screen, error: null }),

  // Room
  setRoom: (room) => set({ room }),
  
  addPlayer: (player) => set((state) => {
    if (!state.room) return state;
    const exists = state.room.players.some(p => p.id === player.id);
    if (exists) return state;
    return {
      room: {
        ...state.room,
        players: [...state.room.players, player],
      },
    };
  }),

  removePlayer: (playerId) => set((state) => {
    if (!state.room) return state;
    return {
      room: {
        ...state.room,
        players: state.room.players.filter(p => p.id !== playerId),
      },
    };
  }),

  updatePlayer: (player) => set((state) => {
    if (!state.room) return state;
    return {
      room: {
        ...state.room,
        players: state.room.players.map(p => p.id === player.id ? player : p),
      },
    };
  }),

  updatePlayerReady: (playerId: UserId, isReady: boolean) => set((state) => {
    if (!state.room) return state;
    return {
      room: {
        ...state.room,
        players: state.room.players.map(p => 
          p.id === playerId ? { ...p, isReady } : p
        ),
      },
    };
  }),

  // Game
  setGameStarted: (gameId, totalQuestions, resultsDisplayTime) => set({
    gameId,
    totalQuestions,
    resultsDisplayTime: resultsDisplayTime || 12,
    gameStatus: 'preparing',
    screen: 'game',
    currentRound: 0,
    leaderboard: [],
    gameStats: null,
    roundResults: null,
  }),

  setGameStatus: (status) => set({ gameStatus: status, ...(status === 'preparing' ? { screen: 'game' as const } : {}) }),

  setCountdown: (seconds) => set({ 
    gameStatus: 'countdown',
    timeLeft: seconds * 1000,
  }),

  // Question
  setQuestion: ({ questionId, questionText, options, round }) => set({
    gameStatus: 'question',
    questionId,
    questionText: questionText || null,
    options,
    currentRound: round,
    selectedAnswer: null,
    answerSent: false,
    isCorrect: null,
    pointsEarned: 0,
  }),

  selectAnswer: (index) => set({ selectedAnswer: index }),

  setAnswerSent: () => set({ answerSent: true }),

  setTimeLeft: (ms) => set({ timeLeft: ms }),

  // Results
  setRoundResults: (results, myPlayerId) => {
    const myResult = results.playerResults.find(r => r.playerId === myPlayerId);
    set({
      gameStatus: 'results',
      roundResults: results,
      leaderboard: results.leaderboard,
      isCorrect: myResult?.isCorrect ?? null,
      pointsEarned: myResult?.points ?? 0,
    });
  },

  setGameFinished: (leaderboard, stats) => set({
    gameStatus: 'finished',
    screen: 'finished',
    gameStats: stats || null,
    leaderboard,
  }),

  // Errors
  setError: (error) => set({ error }),

  // Reset
  reset: () => set(initialState),

  resetGame: () => set({
    gameId: null,
    gameStatus: 'idle',
    currentRound: 0,
    totalQuestions: 0,
    resultsDisplayTime: 12,
    questionId: null,
    questionText: null,
    options: [],
    selectedAnswer: null,
    answerSent: false,
    timeLeft: 0,
    roundResults: null,
    leaderboard: [],
    gameStats: null,
    isCorrect: null,
    pointsEarned: 0,
  }),
}));